import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Tabs from "../components/ui/Tabs";
import SEO from "../components/SEO";
import { Grid, List, Clock, Eye, ArrowRight, AlertCircle } from "lucide-react";

import NoticiaCardImage from "../components/NoticiaCardImage";

/* ---------------------------
   Mapeamento de Categorias
---------------------------- */

// Categorias principais
const MAIN_CATEGORIES = ["Economia", "Política", "Esportes", "Tecnologia", "Beleza", "Famosos", "Saúde", "Educação", "Ciência", "Crimes", "Games"];

// Mapeamento de categorias para as principais
const CATEGORY_MAP = {
  "Basquete": "Esportes", "Futebol": "Esportes", "Tênis": "Esportes", "Esportes": "Esportes", "Esporte": "Esportes",
  "Entretenimento": "Famosos", "Reality Show": "Famosos", "Famosos": "Famosos",
  "Jogos": "Games", "Games": "Games",
  "Tecnologia": "Tecnologia",
  "Loterias": "Economia", "Economia": "Economia",
  "Política": "Política", "Geopolítica": "Política",
  "Beleza": "Beleza",
  "Saúde": "Saúde",
  "Educação": "Educação",
  "Ciência": "Ciência", "Ciencia": "Ciência",
  "Crimes": "Crimes", "Segurança": "Crimes",
};

// Normaliza uma categoria para uma das principais
function normalizeCategory(cat) {
  if (!cat) return null;
  const normalized = cat.trim();
  return CATEGORY_MAP[normalized] || normalized;
}

// Retorna todas as subcategorias que mapeiam para uma categoria principal
function getSubcategories(mainCat) {
  const subs = [];
  for (const [sub, main] of Object.entries(CATEGORY_MAP)) {
    if (main === mainCat) subs.push(sub);
  }
  return subs.length > 0 ? subs : [mainCat];
}

/* ---------------------------
   Página
---------------------------- */

const PAGE_SIZE = 10;

export default function NoticiasPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("list");
  const [activeCategory, setActiveCategory] = useState("all");
  const [noticias, setNoticias] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const hasMore = noticias.length < total;

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await axios.get("/news/meta/categorias");
      if (res.data.success && Array.isArray(res.data.data)) {
        const normalized = res.data.data
          .map(normalizeCategory)
          .filter(Boolean);

        const uniqueCategories = Array.from(new Set(normalized))
          .filter(cat => MAIN_CATEGORIES.includes(cat))
          .sort((a, b) => {
            const indexA = MAIN_CATEGORIES.indexOf(a);
            const indexB = MAIN_CATEGORIES.indexOf(b);
            return indexA - indexB;
          });

        setCategorias(uniqueCategories);
      }
    } catch (err) {
      console.error("Erro ao carregar categorias", err);
    }
  }, []);

  const fetchNoticias = useCallback(async (pageNum = 0, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params = {
        limit: PAGE_SIZE,
        offset: pageNum * PAGE_SIZE,
      };

      // Ordena por views no backend
      if (activeCategory === "most_viewed") {
        params.sort = "views";
      } else if (activeCategory !== "all") {
        // Envia todas as subcategorias que mapeiam para a categoria principal
        params.categorias = getSubcategories(activeCategory).join(",");
      }

      const res = await axios.get("/news", { params });

      if (res.data?.success) {
        const data = res.data.data;

        if (append) {
          setNoticias(prev => [...prev, ...data]);
        } else {
          setNoticias(data);
        }
        setTotal(res.data.total || 0);
        setPage(pageNum);
      } else {
        if (!append) setNoticias([]);
      }
    } catch (err) {
      console.error("Erro ao carregar notícias", err);
      if (!append) setError("Não foi possível carregar as notícias. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeCategory]);

  // Reset e busca quando muda categoria
  useEffect(() => {
    setNoticias([]);
    setPage(0);
    setTotal(0);
    fetchNoticias(0, false);
  }, [fetchNoticias]);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchNoticias(page + 1, true);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      // Extrai apenas YYYY-MM-DD de qualquer formato (ISO, timestamp, etc.)
      const match = String(dateStr).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return dateStr;

      const [, year, month, day] = match.map(Number);
      return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const capitalizeCategory = (cat) => {
    if (!cat) return "";
    return cat
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const tabs = [
    { id: "all", label: "Todas" },
    { id: "most_viewed", label: "Mais Lidas" },
    ...categorias.map((cat) => ({ id: cat, label: capitalizeCategory(cat) }))
  ];

  if (loading && noticias.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500">Carregando notícias...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-4">
        <AlertCircle size={48} className="text-red-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Ops! Algo deu errado.</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => fetchNoticias()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="Notícias"
        description="Notícias sobre economia, política e o dia a dia do Brasil, organizadas por assunto e cidade."
        path="/noticias"
      />
      <div className="mb-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Notícias</h1>
            <p className="text-gray-500">Acompanhe as últimas atualizações do mercado</p>
          </div>

          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg self-start sm:self-auto">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-md transition-all ${viewMode === "grid" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              aria-label="Visualização em grade"
            >
              <Grid size={20} />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md transition-all ${viewMode === "list" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              aria-label="Visualização em lista"
            >
              <List size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto pb-2">
          <Tabs tabs={tabs} activeTab={activeCategory} onChange={setActiveCategory} />
        </div>
      </div>

      {noticias.length === 0 && !loading ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <p className="text-gray-500 text-lg">Nenhuma notícia encontrada para esta categoria.</p>
          <button onClick={() => setActiveCategory("all")} className="mt-4 text-blue-600 font-medium hover:underline">
            Ver todas as notícias
          </button>
        </div>
      ) : (
        <>
          <div className={viewMode === "grid" ? "grid md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
            {noticias.map((noticia) => (
              <Card
                key={noticia.slug}
                onClick={() => navigate(`/noticias/${noticia.slug}`)}
                className="cursor-pointer group hover:shadow-xl transition-all duration-300 border border-transparent hover:border-blue-100"
              >
                <div className={viewMode === "list" ? "flex flex-col sm:flex-row gap-6" : "flex flex-col h-full"}>
                  <NoticiaCardImage
                    imageUrl={noticia.imageUrl}
                    title={noticia.title}
                    viewMode={viewMode}
                    slug={noticia.slug}
                  />

                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-3 mb-2 text-xs font-medium">
                      <Badge category={normalizeCategory(noticia.categoria) || noticia.categoria} />
                      <span className="flex items-center gap-1 text-gray-400">
                        <Clock size={12} />
                        {noticia.dataGeracao ? formatDate(noticia.dataGeracao) : "Recente"}
                      </span>
                      <span className="flex items-center gap-1 text-gray-400">
                        <Eye size={12} />
                        {(noticia.views || 0).toLocaleString("pt-BR")}
                      </span>
                    </div>

                    <h3
                      className={`font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2 ${viewMode === "list" ? "text-xl" : "text-lg line-clamp-2"
                        }`}
                    >
                      {noticia.title}
                    </h3>

                    <p className="text-gray-600 text-sm line-clamp-2 mb-4 flex-grow">{noticia.summary}</p>

                    <div className="flex items-center text-blue-600 font-medium text-sm mt-auto group/link">
                      Ler matéria completa
                      <ArrowRight size={16} className="ml-1 transition-transform group-hover/link:translate-x-1" />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Paginação */}
          <div className="mt-8 text-center space-y-3">
            <p className="text-sm text-gray-400">
              Exibindo {noticias.length} de {total} notícias
            </p>

            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loadingMore ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Carregando...
                  </span>
                ) : (
                  "Carregar mais notícias"
                )}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
